import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import {
  useSelectedCallerID,
  useSelectedCall,
} from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import type { RootState } from '../../../../app';
import type { ExistingRequest, FormValuesWithSelect } from '../../types';
import { DEFAULT_FORM_VALUES } from '../constants';
import { formatAddressFromObject } from '../utils';
import { formatCoordinates, parseCoordinates } from '../validation';

interface Location {
  latitude: number;
  longitude: number;
  addressObject?: Record<string, string | undefined>;
  rgAddress?: string;
}

const useInitialFormValues = (): FormValuesWithSelect => {
  const selectedCallerID = useSelectedCallerID();
  const selectedCall = useSelectedCall();
  const existingRequest = useSelector(
    selectExistingRequest,
  ) as ExistingRequest | null;
  const formPageToShow = useSelector(
    (state: RootState) => state.airAmbulance.formPageToShow,
  );

  return useMemo(() => {
    // If there's an existing request, populate the form with its data
    if (existingRequest?.body) {
      const { body } = existingRequest;
      // Handle different possible structures of the location data
      let latitude: number | undefined;
      let longitude: number | undefined;
      let pickupLocation: string | undefined;

      // First, check if coordinates are already formatted as a string
      if (body.pickup_coordinates || body.trip_data?.pickup_coordinates) {
        const coordString =
          body.pickup_coordinates || body.trip_data?.pickup_coordinates;
        const coords = parseCoordinates(coordString);
        if (coords) {
          latitude = coords.latitude;
          longitude = coords.longitude;
          pickupLocation =
            body.pickup_location || body.trip_data?.pickup_location || '';
        }
      }
      // Check if trip_data exists and has location
      else if (body.trip_data?.location) {
        const location = body.trip_data.location;
        latitude = parseFloat(location.latitude);
        longitude = parseFloat(location.longitude);
        pickupLocation = body.trip_data.pickup_location || '';
      }
      // Check if location exists directly on the body
      else if (body.location) {
        const location = body.location;
        latitude =
          typeof location.latitude === 'string'
            ? parseFloat(location.latitude)
            : location.latitude;
        longitude =
          typeof location.longitude === 'string'
            ? parseFloat(location.longitude)
            : location.longitude;
        pickupLocation = body.pickup_location || '';
      }
      // Check if lat/lng exist directly on the body
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      else if ((body as any).latitude && (body as any).longitude) {
        // eslint-disable-next-line no-console
        console.error(
          '[useInitialFormValues] Legacy location format detected: latitude/longitude directly on body. Please update types.',
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        latitude = parseFloat((body as any).latitude);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        longitude = parseFloat((body as any).longitude);
        pickupLocation = body.pickup_location || '';
      }

      // Format coordinates for the form
      const formattedCoordinates =
        latitude !== undefined &&
        longitude !== undefined &&
        !isNaN(latitude) &&
        !isNaN(longitude)
          ? formatCoordinates(latitude, longitude)
          : '';
      // Get other form values from trip_data or directly from body
      const tripData = body.trip_data || body;

      // Map the existing request data to form fields
      return {
        ...DEFAULT_FORM_VALUES,
        pickup_coordinates: formattedCoordinates,
        pickup_location: pickupLocation || '',
        pickup_details: tripData.pickup_details || '',
        ground_contact: tripData.ground_contact || '',
        patient_info: tripData.patient_info || '',
        incident_info: tripData.incident_info || '',
        request_reason: tripData.trip_reason
          ? { value: tripData.trip_reason, label: tripData.trip_reason }
          : 'Select',
        multiple_units_requested: tripData.multiple_units_requested
          ? 'Yes'
          : 'No',
        launch_type: tripData.launch_type || 'Immediate',
        air_service_declined: tripData.weather_decline ? 'Yes' : 'No',
        air_service_responding: tripData.another_carrier ? 'Yes' : 'No',
      };
    }

    // If no existing request, use location from selected call if available
    if (!selectedCallerID || !selectedCall) {
      return DEFAULT_FORM_VALUES;
    }

    const latestLocation = getMostRecentLoc(selectedCall) as Location | null;

    if (!latestLocation) {
      return DEFAULT_FORM_VALUES;
    }

    const formattedAddress = latestLocation.addressObject
      ? formatAddressFromObject(latestLocation.addressObject)
      : latestLocation.rgAddress || '';

    return {
      ...DEFAULT_FORM_VALUES,
      pickup_coordinates: formatCoordinates(
        latestLocation.latitude,
        latestLocation.longitude,
      ),
      pickup_location: formattedAddress,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedCall intentionally excluded: including it would reset the address every time Hermes updates a new location
  }, [selectedCallerID, existingRequest, formPageToShow]);
};

export default useInitialFormValues;
